import { QIMapLoader } from "../../../components";
import "./VehicleMap.scss";
import { useEffect, useRef, useState } from "react";
import { Icon, Style } from "ol/style.js";
import { fromLonLat } from "ol/proj";
import { useSelector } from "react-redux";
import { data_point } from "../assets";
import { getMovementStatusCounts, getRotation, MOVEMENT_STATUS_PRESENTATION } from "../utils";
import sprite from "../../../assets/icons.svg";
import { getLocalizedString } from "../../../shared/translation";

const MovementStatusLegend = ({ position = "left", data = [], showStatusCounts = true }) => {
  const [isOpen, setIsOpen] = useState(true);
  const statusCounts = showStatusCounts ? getMovementStatusCounts(data) : {};

  return (
    <div className={`movement-status-legend movement-status-legend--${position}`}>

      {isOpen && (
        <ul className="movement-status-legend_list">
          {Object.entries(MOVEMENT_STATUS_PRESENTATION).map(([key, status]) => (
            <li key={key} className="movement-status-legend_item">
              <svg className="movement-status-legend_icon" style={{ color: status.color }}>
                <use href={`${sprite}#${status.icon}`}></use>
              </svg>
              <span className="movement-status-legend_label">
                {getLocalizedString(status.localizationKey, status.defaultLabel)}
              </span>
              {showStatusCounts && key !== "offline" && (
                <span className="movement-status-legend_count">{statusCounts[key]}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const VehicleMap = ({
  setMapRef,
  dockAlign,
  mapExpand,
  mapRef,
  deviceId,
  selectedRowId,
  maximizeMinimize,
  mapProvider,
  mapboxAccessToken,
  googleMapsApiKey,
  mapPoint,
  selectedVehiclePlate,
  currentMapCenter,
  currentMapZoom,
  userHasAdjustedMap = false,
  resetMapToInitialState,
  legendPosition = "left",
  mapControlsPosition = "right",
  statusData = [],
  showStatusCounts = true,
}) => {
  const restoredMapRef = useRef(null);
  const selectedPointRef = useRef(null);
  let liveTrackData = useSelector((state) => state.common.liveTrackData);

  // History unmounts this map; restore the selected vehicle's viewport on the new instance.
  useEffect(() => {
    if (
      !selectedVehiclePlate ||
      !mapRef ||
      !currentMapCenter ||
      currentMapZoom == null ||
      restoredMapRef.current === mapRef
    ) {
      return;
    }

    if (mapProvider === "qiMap") {
      const view = mapRef.getMapReference()?.getView();
      view?.setCenter(currentMapCenter);
      view?.setZoom(currentMapZoom);
    } else if (mapProvider === "mapbox") {
      mapRef.jumpTo({ center: currentMapCenter, zoom: currentMapZoom });
    } else if (mapProvider === "google") {
      mapRef.setCenter(currentMapCenter);
      mapRef.setZoom(currentMapZoom);
    }
    restoredMapRef.current = mapRef;
  }, [mapRef, mapProvider, selectedVehiclePlate, currentMapCenter, currentMapZoom]);

  useEffect(() => {
    if (mapProvider !== "qiMap" || !mapRef) return;

    const layerName = "selected_track_point";
    mapRef.layer?.[layerName]?.getSource()?.clear();
    if (deviceId && mapPoint?.lat && mapPoint?.lng && mapPoint?.is_valid && liveTrackData?.length > 1) {
      if (!mapRef.layer?.[layerName]) mapRef.addLayer({ name: layerName });
      mapRef.layer?.[layerName]?.setZIndex?.(10);
      mapRef.drawTrack({
        layerName,
        fitWithinView: false,
        trackType: "point",
        data: [{ coordinates: [mapPoint] }],
        style: {
          point: {
            style: (feature) => new Style({
              zIndex: 999,
              image: new Icon({
                crossOrigin: "anonymous",
                src: data_point,
                imgSize: [34, 40],
                rotation: getRotation(feature.direction),
              }),
            }),
          },
        },
      });
    }

    return () => mapRef.layer?.[layerName]?.getSource()?.clear();
  }, [mapRef, deviceId, mapProvider, mapPoint, liveTrackData?.length]);

  useEffect(() => {
    if (mapProvider === "qiMap" && mapRef && mapPoint?.is_valid && mapPoint?.lat && mapPoint?.lng) {
      const selectedPoint = `${selectedRowId}:${mapPoint.lng}:${mapPoint.lat}`;
      const selectionChanged = selectedPointRef.current !== selectedPoint;
      selectedPointRef.current = selectedPoint;
      if (!selectionChanged && userHasAdjustedMap) return;

      const view = mapRef.getMapReference()?.getView();
      const selectedCenter = fromLonLat([mapPoint.lng, mapPoint.lat]);
      const center = view?.getCenter?.();
      if (center?.[0] !== selectedCenter[0] || center?.[1] !== selectedCenter[1]) {
        view?.setCenter(selectedCenter);
      }
    }
  }, [mapRef, mapProvider, mapPoint, selectedRowId, currentMapCenter, userHasAdjustedMap]);

  return (
    <div className={`vehicle-map-container ${dockAlign && "qiMap-split"}`}>
      {/* Reset Map Button */}
      {/* {(deviceId || selectedRowId) && (
        <div className="map-controls">
          <div className="reset-map-btn" onClick={resetMapToInitialState} title="Reset Map">
            Reset
          </div>
        </div>
      )} */}

      <MovementStatusLegend
        position={legendPosition}
        data={statusData}
        showStatusCounts={showStatusCounts}
      />

      {/* Only mount the map once the provider has been resolved from config.
          Rendering while `mapProvider` is still undefined would let QIMapLoader
          fall back to its "qiMap" default and initialize OSM, which is exactly
          the flash-to-OSM seen on refresh before the Redux config is hydrated. */}
      {mapProvider && (
        <QIMapLoader
          dockAlign={dockAlign}
          containerId="device-map-container"
          setMapRef={setMapRef}
          mapExpand={mapExpand}
          mapRef={mapRef}
          deviceId={deviceId}
          selectedRowId={selectedRowId}
          maximizeMinimize={maximizeMinimize}
          mapProvider={mapProvider}
          mapboxAccessToken={mapboxAccessToken}
          googleMapsApiKey={googleMapsApiKey}
          mapControlsPosition={mapControlsPosition}
        />
      )}
    </div>
  );
};
